import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCRUAL_BASES,
  ENTITLEMENT_PERIODS,
  LeaveTypeSchema,
  type LeaveTypeDto,
} from '@taqon/contracts';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { daysLabel, LEAVE_TYPES, messageOf } from './leave';

const PERIOD_LABELS: Readonly<Record<string, string>> = {
  calendar_year: 'Calendar year',
  service_year: 'Service year (from the joining date)',
};
const ACCRUAL_LABELS: Readonly<Record<string, string>> = {
  upfront: 'Upfront (whole entitlement at the start of the period)',
  monthly_pro_rata: 'Monthly pro rata (per whole month)',
};

/**
 * Leave types (`/hr/leave-types`; FRD-HRP-025, HRP-026): configuration HR maintains. Every
 * entitlement rule value is entered explicitly — the sources establish none, so nothing is
 * pre-filled — and must be validated against the authoritative source before production use
 * (BRD §23). Types are deactivated, never deleted.
 *
 * Short leave (FRD-HRP-080, CLR-41) is a type counted in hours: a monthly limit, the most hours of
 * one request, the hours of a working day and the leave type a longer request becomes. Its form
 * starts with the values management decided (8, 4 and 8 hours); they stay editable.
 */
export function LeaveTypesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'hr.leave_type:create');
  const canChange = hasCapability(session.data, 'hr.leave_type:change');
  const types = useQuery({
    queryKey: ['hr', 'leave-types'],
    queryFn: () => getPage(`${LEAVE_TYPES}?pageSize=200&sort=code`, LeaveTypeSchema),
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<LeaveTypeDto | null>(null);
  const [toggling, setToggling] = useState<LeaveTypeDto | null>(null);
  const rows = types.data?.items ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Leave types"
        description="The leave employees may request and the entitlement rule of each."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New leave type
            </Button>
          ) : undefined
        }
      />
      <Alert severity="info" variant="outlined">
        Entitlement values, accrual, probation and carry-forward are configuration. The sources do
        not establish them: validate each value against the applicable authoritative source before
        production use.
      </Alert>
      {types.isError && <ErrorState message={types.error.message} />}
      <Section>
        {types.isPending && <LoadingState label="Loading leave types" />}
        {types.data !== undefined && rows.length === 0 && (
          <EmptyState
            title="No leave types yet"
            detail="Leave types are configured by HR; none is provided by the system."
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Leave types">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Entitlement</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Accrual</TableCell>
                <TableCell align="right">Probation (months)</TableCell>
                <TableCell align="right">Carry forward</TableCell>
                <TableCell>Negative balance</TableCell>
                <TableCell>Reason and document</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{t.code}</TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    {t.countedIn === 'hours'
                      ? `${daysLabel(t.monthlyLimitHours ?? '')} hours a month, up to ${daysLabel(t.maxHoursPerRequest ?? '')} a request`
                      : t.balanceTracked
                        ? `${daysLabel(t.annualEntitlementDays ?? '')} days a year`
                        : 'No balance'}
                  </TableCell>
                  <TableCell>
                    {t.entitlementPeriod === null ? '—' : PERIOD_LABELS[t.entitlementPeriod]}
                  </TableCell>
                  <TableCell>
                    {t.accrualBasis === null
                      ? '—'
                      : t.accrualBasis === 'upfront'
                        ? 'Upfront'
                        : 'Monthly pro rata'}
                  </TableCell>
                  <TableCell align="right">{t.probationMonths ?? '—'}</TableCell>
                  <TableCell align="right">
                    {t.carryForwardMaxDays === null ? '—' : daysLabel(t.carryForwardMaxDays)}
                  </TableCell>
                  <TableCell>
                    {t.allowNegativeBalance === null
                      ? '—'
                      : t.allowNegativeBalance
                        ? 'Allowed'
                        : 'Refused'}
                  </TableCell>
                  <TableCell>
                    {t.supportingRequiredAboveDays === null
                      ? '—'
                      : Number(t.supportingRequiredAboveDays) === 0
                        ? 'Always'
                        : `Above ${daysLabel(t.supportingRequiredAboveDays)} ${Number(t.supportingRequiredAboveDays) === 1 ? 'day' : 'days'}`}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={t.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        aria-label={`Edit ${t.code}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setEditing(t);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color={t.status === 'active' ? 'warning' : 'primary'}
                        aria-label={`${t.status === 'active' ? 'Deactivate' : 'Activate'} ${t.code}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setToggling(t);
                        }}
                      >
                        {t.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {creating && (
        <LeaveTypeDialog
          types={rows}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {editing !== null && (
        <LeaveTypeDialog
          key={`${editing.id}:${String(editing.version)}`}
          leaveType={editing}
          types={rows}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {toggling !== null && (
        <StatusDialog
          leaveType={toggling}
          onClose={() => {
            setToggling(null);
          }}
        />
      )}
    </Stack>
  );
}

function StatusDialog({ leaveType, onClose }: { leaveType: LeaveTypeDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = leaveType.status === 'active' ? 'inactive' : 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${LEAVE_TYPES}/${leaveType.id}`,
        { status: next },
        LeaveTypeSchema,
        session.data?.csrfToken ?? '',
        leaveType.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave-types'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`${next === 'inactive' ? 'Deactivate' : 'Activate'} ${leaveType.code}?`}
      message={
        next === 'inactive'
          ? 'It can no longer be requested. Existing requests keep it.'
          : 'Employees can request it again.'
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      tone={next === 'inactive' ? 'warning' : 'primary'}
      busy={change.isPending}
      error={change.isError ? change.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        change.mutate();
      }}
    />
  );
}

const DAYS = /^\d{1,3}(\.[05])?$/;

interface RuleValues {
  annualEntitlementDays: string;
  entitlementPeriod: string;
  accrualBasis: string;
  probationMonths: string;
  carryForwardMaxDays: string;
  allowNegativeBalance: string;
}

function ruleOf(t: LeaveTypeDto | undefined): RuleValues {
  return {
    annualEntitlementDays:
      t?.annualEntitlementDays == null ? '' : daysLabel(t.annualEntitlementDays),
    entitlementPeriod: t?.entitlementPeriod ?? '',
    accrualBasis: t?.accrualBasis ?? '',
    probationMonths: t?.probationMonths == null ? '' : String(t.probationMonths),
    carryForwardMaxDays: t?.carryForwardMaxDays == null ? '' : daysLabel(t.carryForwardMaxDays),
    allowNegativeBalance:
      t?.allowNegativeBalance == null ? '' : t.allowNegativeBalance ? 'yes' : 'no',
  };
}

interface ShortLeaveValues {
  monthlyLimitHours: string;
  maxHoursPerRequest: string;
  hoursPerDay: string;
  conversionLeaveTypeId: string;
}

/** CLR-41 (9 Oct 2026): 8 hours a month, 4 hours a request, an 8-hour working day. */
const SHORT_LEAVE_DECIDED: ShortLeaveValues = {
  monthlyLimitHours: '8',
  maxHoursPerRequest: '4',
  hoursPerDay: '8',
  conversionLeaveTypeId: '',
};

function shortLeaveOf(t: LeaveTypeDto | undefined): ShortLeaveValues {
  if (t?.countedIn !== 'hours') return SHORT_LEAVE_DECIDED;
  return {
    monthlyLimitHours: daysLabel(t.monthlyLimitHours ?? ''),
    maxHoursPerRequest: daysLabel(t.maxHoursPerRequest ?? ''),
    hoursPerDay: daysLabel(t.hoursPerDay ?? ''),
    conversionLeaveTypeId: t.conversionLeaveTypeId ?? '',
  };
}

/**
 * Create (no `leaveType`) or change a leave type. For a tracked type every rule field is required
 * and starts empty: the administrator states each value. A type counted in hours is short leave;
 * what a type is counted in cannot change once it exists.
 */
function LeaveTypeDialog({
  leaveType,
  types,
  onClose,
}: {
  leaveType?: LeaveTypeDto;
  /** Every leave type, for the type a longer short-leave request becomes. */
  types: readonly LeaveTypeDto[];
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const creating = leaveType === undefined;
  const [code, setCode] = useState(leaveType?.code ?? '');
  const [name, setName] = useState(leaveType?.name ?? '');
  const [countedIn, setCountedIn] = useState(leaveType?.countedIn ?? 'days');
  const inHours = countedIn === 'hours';
  const [trackedChoice, setTracked] = useState(leaveType?.balanceTracked ?? true);
  // Short leave has a monthly limit, never an entitlement and a balance.
  const tracked = trackedChoice && !inHours;
  const [rule, setRule] = useState<RuleValues>(ruleOf(leaveType));
  const [short, setShort] = useState<ShortLeaveValues>(shortLeaveOf(leaveType));
  // CLR-46: above how many days a reason and a supporting document are required; blank never.
  const [evidenceAbove, setEvidenceAbove] = useState(
    leaveType?.supportingRequiredAboveDays == null
      ? ''
      : daysLabel(leaveType.supportingRequiredAboveDays),
  );
  const evidenceOk = inHours || evidenceAbove === '' || DAYS.test(evidenceAbove);
  const evidenceBody = inHours
    ? {}
    : evidenceAbove === ''
      ? creating
        ? {}
        : { supportingRequiredAboveDays: null }
      : { supportingRequiredAboveDays: evidenceAbove };
  const setShortField = (field: keyof ShortLeaveValues) => (value: string) => {
    setShort((current) => ({ ...current, [field]: value }));
  };
  const conversionTypes = types.filter(
    (t) =>
      t.countedIn === 'days' &&
      t.id !== leaveType?.id &&
      (t.status === 'active' || t.id === short.conversionLeaveTypeId),
  );
  const hoursOf = (value: string) => (DAYS.test(value) && Number(value) > 0 ? Number(value) : NaN);
  const limit = hoursOf(short.monthlyLimitHours);
  const most = hoursOf(short.maxHoursPerRequest);
  const day = hoursOf(short.hoursPerDay);
  const mostTooHigh = most > limit || most > day;
  const shortComplete =
    !inHours ||
    (!Number.isNaN(limit) &&
      !Number.isNaN(most) &&
      !Number.isNaN(day) &&
      day <= 24 &&
      !mostTooHigh &&
      short.conversionLeaveTypeId !== '');
  const shortBody = inHours
    ? {
        ...(creating ? { countedIn: 'hours' } : {}),
        monthlyLimitHours: short.monthlyLimitHours,
        maxHoursPerRequest: short.maxHoursPerRequest,
        hoursPerDay: short.hoursPerDay,
        conversionLeaveTypeId: short.conversionLeaveTypeId,
      }
    : {};
  const set = (field: keyof RuleValues) => (value: string) => {
    setRule((current) => ({ ...current, [field]: value }));
  };

  const probationOk = /^\d{1,3}$/.test(rule.probationMonths) && Number(rule.probationMonths) <= 120;
  const ruleComplete =
    !tracked ||
    (DAYS.test(rule.annualEntitlementDays) &&
      rule.entitlementPeriod !== '' &&
      rule.accrualBasis !== '' &&
      probationOk &&
      DAYS.test(rule.carryForwardMaxDays) &&
      rule.allowNegativeBalance !== '');
  const ruleBody = tracked
    ? {
        annualEntitlementDays: rule.annualEntitlementDays,
        entitlementPeriod: rule.entitlementPeriod,
        accrualBasis: rule.accrualBasis,
        probationMonths: Number(rule.probationMonths),
        carryForwardMaxDays: rule.carryForwardMaxDays,
        allowNegativeBalance: rule.allowNegativeBalance === 'yes',
      }
    : creating
      ? {}
      : {
          annualEntitlementDays: null,
          entitlementPeriod: null,
          accrualBasis: null,
          probationMonths: null,
          carryForwardMaxDays: null,
          allowNegativeBalance: null,
        };
  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(
            LEAVE_TYPES,
            {
              code: code.trim(),
              name: name.trim(),
              balanceTracked: tracked,
              ...ruleBody,
              ...shortBody,
              ...evidenceBody,
            },
            LeaveTypeSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${LEAVE_TYPES}/${leaveType.id}`,
            {
              name: name.trim(),
              balanceTracked: tracked,
              ...ruleBody,
              ...shortBody,
              ...evidenceBody,
            },
            LeaveTypeSchema,
            session.data?.csrfToken ?? '',
            leaveType.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave-types'] });
      await queryClient.invalidateQueries({ queryKey: ['hr', 'leave'] });
      onClose();
    },
  });

  const daysField = (label: string, field: keyof RuleValues, helper: string) => (
    <TextField
      required
      label={label}
      value={rule[field]}
      error={rule[field] !== '' && !DAYS.test(rule[field])}
      helperText={rule[field] !== '' && !DAYS.test(rule[field]) ? 'Days in half-day steps' : helper}
      onChange={(e) => {
        set(field)(e.target.value.trim());
      }}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      fullWidth
    />
  );

  const hoursField = (
    label: string,
    field: keyof ShortLeaveValues,
    helper: string,
    invalid: boolean,
  ) => (
    <TextField
      required
      label={label}
      value={short[field]}
      error={(short[field] !== '' && Number.isNaN(hoursOf(short[field]))) || invalid}
      helperText={
        short[field] !== '' && Number.isNaN(hoursOf(short[field]))
          ? 'Hours in half-hour steps, above zero'
          : helper
      }
      onChange={(e) => {
        setShortField(field)(e.target.value.trim());
      }}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      fullWidth
    />
  );

  return (
    <FormDialog
      open
      title={creating ? 'New leave type' : `Edit leave type ${leaveType.code}`}
      submitLabel={creating ? 'Create leave type' : 'Save changes'}
      canSubmit={
        name.trim() !== '' &&
        (!creating || code.trim() !== '') &&
        ruleComplete &&
        shortComplete &&
        evidenceOk
      }
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Code"
          required
          autoFocus={creating}
          disabled={!creating}
          value={code}
          helperText={creating ? 'Upper case, e.g. ANNUAL. It cannot change later.' : undefined}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
          }}
          fullWidth
        />
        <TextField
          label="Name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          fullWidth
        />
      </Stack>
      <TextField
        select
        required
        label="Counted in"
        value={countedIn}
        disabled={!creating}
        helperText={
          creating
            ? 'Hours is short leave: a few hours on one day, within a monthly limit. It cannot change later.'
            : undefined
        }
        onChange={(e) => {
          setCountedIn(e.target.value === 'hours' ? 'hours' : 'days');
        }}
      >
        <MenuItem value="days">Days</MenuItem>
        <MenuItem value="hours">Hours (short leave)</MenuItem>
      </TextField>
      {inHours && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {hoursField(
              'Monthly limit (hours)',
              'monthlyLimitHours',
              'Most hours in a calendar month; a request beyond it is refused',
              false,
            )}
            {hoursField(
              'Most hours in one request',
              'maxHoursPerRequest',
              mostTooHigh
                ? 'No more than the monthly limit or a working day'
                : 'A longer request becomes the leave type below',
              mostTooHigh,
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {hoursField(
              'Hours in a working day',
              'hoursPerDay',
              day > 24 ? 'At most 24' : 'Turns the hours of a longer request into days',
              day > 24,
            )}
            <TextField
              select
              required
              label="Longer requests become"
              value={short.conversionLeaveTypeId}
              helperText={
                conversionTypes.length === 0
                  ? 'Create a leave type counted in days first, e.g. annual leave'
                  : 'The whole request is recorded as this leave type'
              }
              onChange={(e) => {
                setShortField('conversionLeaveTypeId')(e.target.value);
              }}
              fullWidth
            >
              {conversionTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Alert severity="info" variant="outlined">
            Hours not used in a month are not carried to the next. A request above the most hours in
            one request uses none of the month&apos;s hours: it is recorded as the leave type chosen
            here, as half a day when it is within half a working day and one day otherwise.
          </Alert>
        </>
      )}
      {!inHours && (
        <FormControlLabel
          control={
            <Switch
              checked={tracked}
              onChange={(e) => {
                setTracked(e.target.checked);
              }}
            />
          }
          label="Tracks an entitlement and a balance"
        />
      )}
      {tracked && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {daysField('Annual entitlement (days)', 'annualEntitlementDays', 'E.g. 21 or 10.5')}
            {daysField(
              'Carry-forward limit (days)',
              'carryForwardMaxDays',
              'Most days carried into the next period; 0 carries none',
            )}
          </Stack>
          <TextField
            select
            required
            label="Entitlement period"
            value={rule.entitlementPeriod}
            onChange={(e) => {
              set('entitlementPeriod')(e.target.value);
            }}
          >
            {ENTITLEMENT_PERIODS.map((p) => (
              <MenuItem key={p} value={p}>
                {PERIOD_LABELS[p]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            required
            label="Accrual"
            value={rule.accrualBasis}
            onChange={(e) => {
              set('accrualBasis')(e.target.value);
            }}
            helperText="Pro rata rounding is set in Configuration (hr.leave_pro_rata_rounding)."
          >
            {ACCRUAL_BASES.map((a) => (
              <MenuItem key={a} value={a}>
                {ACCRUAL_LABELS[a]}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              label="Probation (months)"
              value={rule.probationMonths}
              error={rule.probationMonths !== '' && !probationOk}
              helperText="No entitlement until this many whole months after joining; 0 for none"
              onChange={(e) => {
                set('probationMonths')(e.target.value.trim());
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              fullWidth
            />
            <TextField
              select
              required
              label="Requests beyond the balance"
              value={rule.allowNegativeBalance}
              onChange={(e) => {
                set('allowNegativeBalance')(e.target.value);
              }}
              fullWidth
            >
              <MenuItem value="no">Refused</MenuItem>
              <MenuItem value="yes">Allowed (negative balance)</MenuItem>
            </TextField>
          </Stack>
        </>
      )}
      {!inHours && (
        <TextField
          label="Reason and supporting document required above (days)"
          value={evidenceAbove}
          error={!evidenceOk}
          helperText={
            !evidenceOk
              ? 'Days in half-day steps'
              : 'Blank: never. 0: always. E.g. 1 for sick leave of more than one day'
          }
          onChange={(e) => {
            setEvidenceAbove(e.target.value.trim());
          }}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
      )}
      {!creating && leaveType.balanceTracked && !tracked && (
        <Alert severity="warning" variant="outlined">
          The type will no longer have a balance; its rule values are cleared.
        </Alert>
      )}
      {!creating && tracked && (
        <Alert severity="info" variant="outlined">
          Balances are derived from the rule, so a change applies to every period, past ones
          included. The change is recorded in the audit trail.
        </Alert>
      )}
    </FormDialog>
  );
}
